import React from "react";
import { css } from "aphrodite";
import styles from "./styles";
import { Typography, IconButton } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const getNumericStep = (field) => {
  if (field.step != null) {
    return Number(field.step);
  }
  return field.type === 1 ? 1 : 0.01;
};

const getDecimalPlaces = (step) => {
  const stepString = String(step);
  if (!stepString.includes(".")) {
    return 0;
  }
  return stepString.split(".")[1].length;
};

const applyStepAndBounds = (num, field) => {
  const min = field.minValue != null ? Number(field.minValue) : 0;
  const max = field.maxValue != null ? Number(field.maxValue) : undefined;
  const step = getNumericStep(field);
  let result = num;

  if (max != null && result > max) {
    result = max;
  }
  if (result < min) {
    result = min;
  }
  if (field.type === 1) {
    result = Math.round(result);
  } else if (field.type === 2) {
    const decimals = getDecimalPlaces(step);
    result = Number(result.toFixed(decimals));
  }

  return result;
};

const getNumericValue = (value, field) => {
  const min = field.minValue != null ? Number(field.minValue) : 0;
  const parsed = value === "" || value == null ? min : Number(value);
  return isNaN(parsed) ? min : parsed;
};

const NumericDynamicField = ({
  field,
  value,
  hasError,
  onChange,
  onBlur,
}) => {
  const step = getNumericStep(field);
  const min = field.minValue != null ? Number(field.minValue) : undefined;
  const max = field.maxValue != null ? Number(field.maxValue) : undefined;
  const currentValue = getNumericValue(value, field);
  const isAtMin = min != null && currentValue <= min;
  const isAtMax = max != null && currentValue >= max;

  const handleStep = (direction) => {
    const next = direction === "up" ? currentValue + step : currentValue - step;
    const adjusted = applyStepAndBounds(next, field);
    onChange(String(adjusted));
    onBlur(String(adjusted));
  };

  const handleInputChange = (event) => {
    onChange(event.target.value);
  };

  const handleInputBlur = (event) => {
    onBlur(event.target.value);
  };

  return (
    <div className={css(styles.numberFieldGroup)}>
      <div
        className={css(
          styles.numberInputWrapper,
          !field.suffix && styles.numberInputWrapperRounded,
          hasError && styles.numberInputError
        )}
      >
        <input
          type="text"
          inputMode={field.type === 1 ? "numeric" : "decimal"}
          className={css(styles.numberInput)}
          value={value}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
        />
        <div className={css(styles.stepper)}>
          <IconButton
            type="button"
            size="small"
            className={css(styles.stepperButton, styles.stepperButtonTop)}
            onClick={() => handleStep("up")}
            disabled={isAtMax}
            aria-label="Increase value"
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton
            type="button"
            size="small"
            className={css(styles.stepperButton)}
            onClick={() => handleStep("down")}
            disabled={isAtMin}
            aria-label="Decrease value"
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      {field.suffix && (
        <div className={css(styles.suffixAddon, hasError && styles.numberInputError)}>
          <Typography variant="body1">{field.suffix}</Typography>
        </div>
      )}
    </div>
  );
};

export default NumericDynamicField;
